/**
 * Phase U.3 — AI Sales Dashboard
 * Shows top intents, AI usage breakdown, revenue, marketing audiences.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import {
  Sparkles, RefreshCw, Loader2, Target, TrendingUp, Users, IndianRupee,
  Activity, Brain, Mic, ImageIcon, FileText as DocIcon, Tag, Send,
  Headphones, ShieldCheck, Wrench, ThumbsUp,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#7c3aed", "#F7941D", "#1a3c5e", "#0EA5E9", "#10b981", "#ef4444", "#a855f7", "#f59e0b"];
const KIND_ICON = {
  analyze: Brain,
  draft_reply: Sparkles,
  objection_handler: TrendingUp,
  recommend_package: Tag,
  auto_qualify: Target,
  transcribe_voice: Mic,
  analyze_image: ImageIcon,
  extract_document: DocIcon,
};

const KPI = ({ label, value, sub, Icon, tone = "blue", testid }) => {
  const tones = {
    blue: "from-blue-50 to-blue-100 border-blue-200 text-blue-900",
    orange: "from-amber-50 to-amber-100 border-amber-200 text-amber-900",
    purple: "from-purple-50 to-purple-100 border-purple-200 text-purple-900",
    green: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900",
  };
  return (
    <div data-testid={testid} className={`bg-gradient-to-br ${tones[tone]} rounded-2xl border p-4 flex items-center gap-3`}>
      <div className="bg-white/70 rounded-xl p-2.5"><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-[10px] uppercase font-bold tracking-wide opacity-80">{label}</div>
        <div className="text-2xl font-extrabold leading-tight">{value}</div>
        {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
};

export default function AISalesDashboard() {
  const [tab, setTab] = useState("sales"); // "sales" | "support" — A8 spec's Support tab
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [audiences, setAudiences] = useState([]);
  const [supportData, setSupportData] = useState(null);
  const [supportLoading, setSupportLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, a] = await Promise.all([
        api.get(`/admin/ai/sales-dashboard?days=${days}`),
        api.get("/admin/ai/marketing-audiences"),
      ]);
      setData(d.data);
      setAudiences(a.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load AI dashboard");
    } finally {
      setLoading(false);
    }
  }, [days]);

  const loadSupport = useCallback(async () => {
    setSupportLoading(true);
    try {
      const r = await api.get(`/admin/ai/support-dashboard?days=${days}`);
      setSupportData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load support dashboard");
    } finally {
      setSupportLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "support") loadSupport(); }, [tab, loadSupport]);

  const intentBars = useMemo(() => (data?.top_intents || []).map(x => ({
    intent: (x.intent || "").replace(/_/g, " ").slice(0, 14),
    count: x.count,
    lead_score: x.avg_lead_score,
    buying_prob: x.avg_buying_probability,
  })), [data]);

  const usagePie = useMemo(() => {
    const by = data?.ai_usage?.by_kind || {};
    return Object.entries(by).map(([k, v]) => ({ name: k.replace(/_/g, " "), value: v }));
  }, [data]);

  const attribution = data?.ai_reply_attribution || {};
  const attrTotal = Object.values(attribution).reduce((s, n) => s + n, 0) || 0;

  return (
    <div className="space-y-5" data-testid="admin-ai-sales-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1a3c5e] flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-600" /> AI Sales Dashboard
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Last {days} days · Model: {data?.model || "—"}</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="ai-dash-range"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs font-semibold bg-white"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            data-testid="ai-dash-refresh"
            onClick={tab === "support" ? loadSupport : load}
            disabled={tab === "support" ? supportLoading : loading}
            className="px-3 py-1.5 bg-[#1a3c5e] hover:bg-[#0f2940] text-white rounded-lg text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {(tab === "support" ? supportLoading : loading) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      {/* A8: Sales / Support tabs */}
      <div className="flex gap-2" data-testid="ai-dash-tabs">
        <button onClick={() => setTab("sales")} data-testid="ai-dash-tab-sales"
          className={`px-3.5 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 border ${tab === "sales" ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-600 border-gray-200"}`}>
          <TrendingUp className="w-3.5 h-3.5" /> Sales
        </button>
        <button onClick={() => setTab("support")} data-testid="ai-dash-tab-support"
          className={`px-3.5 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 border ${tab === "support" ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-600 border-gray-200"}`}>
          <Headphones className="w-3.5 h-3.5" /> Support
        </button>
      </div>

      {tab === "support" ? (
        <SupportTab data={supportData} loading={supportLoading} days={days} />
      ) : (
      <>
      {!data && loading && (
        <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center text-sm text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-600" />
          Loading AI insights…
        </div>
      )}

      {data && (
        <>
          {/* KPI Row */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <KPI testid="ai-kpi-calls" label="AI calls" value={(data.ai_usage?.total_calls || 0).toLocaleString("en-IN")}
                 sub={`~$${data.ai_usage?.est_cost_usd || 0} est. cost`} Icon={Brain} tone="purple" />
            <KPI testid="ai-kpi-avg-score" label="Avg lead score" value={`${data.avg_lead_score || 0}`}
                 sub="across analysed convs" Icon={Target} tone="orange" />
            <KPI testid="ai-kpi-orders" label="Orders paid" value={data.orders_paid_count || 0}
                 sub="in the window" Icon={Activity} tone="blue" />
            <KPI testid="ai-kpi-revenue" label="Revenue" value={inr(data.revenue_inr || 0)}
                 sub="confirmed orders" Icon={IndianRupee} tone="green" />
            <KPI testid="ai-kpi-cost-per-lead" label="Cost per converted lead"
                 value={data.cost_per_converted_lead_usd != null ? `$${data.cost_per_converted_lead_usd}` : "—"}
                 sub={data.cost_per_converted_lead_usd != null ? "AI spend ÷ orders paid" : "no orders in window"} Icon={IndianRupee} tone="orange" />
          </div>

          {/* Charts row */}
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Top intents */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="ai-dash-intents">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-bold text-[#1a3c5e]">Top Customer Intents</h3>
                  <p className="text-[11px] text-gray-500">Conversion potential by detected intent</p>
                </div>
              </div>
              {intentBars.length === 0 ? (
                <div className="text-xs text-gray-500 italic py-10 text-center">No analysed conversations yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={intentBars} margin={{ top: 5, right: 10, left: -10, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="intent" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" interval={0} height={50} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Conversations" fill="#7c3aed" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="lead_score" name="Avg lead score" fill="#F7941D" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Usage pie */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="ai-dash-usage">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-bold text-[#1a3c5e]">AI Usage Breakdown</h3>
                  <p className="text-[11px] text-gray-500">Where AI compute is being spent</p>
                </div>
              </div>
              {usagePie.length === 0 ? (
                <div className="text-xs text-gray-500 italic py-10 text-center">No AI activity yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={usagePie} dataKey="value" nameKey="name" cx="45%" cy="50%" outerRadius={85} label>
                      {usagePie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Reply attribution */}
          <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="ai-dash-attribution">
            <h3 className="font-bold text-[#1a3c5e] mb-1">AI Reply Attribution</h3>
            <p className="text-[11px] text-gray-500 mb-3">How outbound replies were composed</p>
            {attrTotal === 0 ? (
              <div className="text-xs text-gray-500 italic">No AI-assisted replies sent yet.</div>
            ) : (
              <div className="space-y-2">
                {Object.entries(attribution).sort((a, b) => b[1] - a[1]).map(([source, count]) => {
                  const pct = Math.round((count / attrTotal) * 100);
                  return (
                    <div key={source} data-testid={`attr-row-${source}`}>
                      <div className="flex items-center justify-between text-xs font-semibold mb-0.5">
                        <span className="text-[#1a3c5e]">{source || "manual"}</span>
                        <span className="text-gray-500">{count} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-purple-500 to-orange-400" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Marketing audiences */}
          <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="ai-dash-audiences">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><Send className="w-4 h-4 text-purple-600" /> Marketing Audiences</h3>
                <p className="text-[11px] text-gray-500">Auto-built from AI intent + lead temperature + recommended package</p>
              </div>
              <span className="text-[11px] text-gray-500">{audiences.length} segments</span>
            </div>
            {audiences.length === 0 ? (
              <div className="text-xs text-gray-500 italic py-6 text-center">
                No segments yet. Run &ldquo;Sync to Marketing&rdquo; from the Command Center on any analysed conversation to seed.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-[10px] uppercase text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold">Tag</th>
                      <th className="text-right px-3 py-2 font-bold">Leads</th>
                      <th className="text-left px-3 py-2 font-bold">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audiences.slice(0, 50).map((a) => (
                      <tr key={a.id} className="border-t border-gray-100" data-testid={`audience-row-${a.id}`}>
                        <td className="px-3 py-2 font-semibold text-[#1a3c5e]">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                            <Tag className="w-3 h-3" /> {a.id}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right font-bold">{a.lead_count}</td>
                        <td className="px-3 py-2 text-gray-500">{String(a.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}

function SupportTab({ data, loading, days }) {
  if (loading && !data) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center text-sm text-gray-500" data-testid="ai-support-dash-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-600" />
        Loading support insights…
      </div>
    );
  }
  if (!data) return null;

  const csat = data.csat || {};
  const csatTotal = (csat.up || 0) + (csat.down || 0);

  return (
    <div className="space-y-4" data-testid="ai-support-dashboard">
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <KPI testid="support-kpi-auto-resolution" label="Auto-resolution rate"
             value={`${Math.round((data.auto_resolution_rate || 0) * 100)}%`}
             sub={`of ${data.closed_conversations_considered || 0} closed support convos`} Icon={ShieldCheck} tone="green" />
        <KPI testid="support-kpi-escalation" label="Escalation rate"
             value={`${Math.round((data.escalation_rate || 0) * 100)}%`}
             sub={`of ${data.decisions_total || 0} AI decisions`} Icon={Headphones} tone="orange" />
        <KPI testid="support-kpi-tool-failure" label="Tool-call failure rate"
             value={`${Math.round((data.tool_call_failure_rate || 0) * 100)}%`}
             sub={`of ${data.tool_calls_total || 0} grounding lookups`} Icon={Wrench} tone="blue" />
        <KPI testid="support-kpi-csat" label="CSAT (thumbs)"
             value={csatTotal ? `${Math.round((csat.positive_pct || 0) * 100)}%` : "—"}
             sub={csatTotal ? `${csat.up || 0} up / ${csat.down || 0} down` : "no ratings yet"} Icon={ThumbsUp} tone="purple" />
        <KPI testid="support-kpi-cost-per-resolution" label="Cost per resolved convo"
             value={data.cost_per_resolved_conversation_usd != null ? `$${data.cost_per_resolved_conversation_usd}` : "—"}
             sub={data.cost_per_resolved_conversation_usd != null ? "AI spend ÷ closed convos" : "no closed convos in window"} Icon={IndianRupee} tone="blue" />
      </div>
      <div className="bg-white border border-gray-200 rounded-2xl p-4 text-xs text-gray-500" data-testid="ai-support-dash-note">
        Last {days} days · Auto-resolution = closed support conversations that never required a human takeover. Escalation rate = share of AI decisions on support conversations that were forced to human review. Tool-call failure rate = grounding lookups (order status, requirements, revisions, invoice) that came back unable to answer. CSAT = thumbs given on AI-authored replies from Command Center.
      </div>
    </div>
  );
}
